import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UtensilsIcon, MusicIcon, WindIcon, CalendarIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, DownloadIcon } from 'lucide-react';
const sectionHeader = (tag: string, title: string, accent: string, description?: string) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.8
}} className="text-center mb-16" data-id="element-513">
    <p className="text-[#d4a017]/40 tracking-[0.3em] text-[10px] uppercase mb-5 font-light" data-id="element-514">
      {tag}
    </p>
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#f5f0e8] mb-6 tracking-tight" data-id="element-515">
      {title} <span className="text-[#d4a017]" data-id="element-516">{accent}</span>
    </h2>
    <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#d4a017]/30 to-transparent mx-auto mb-6" data-id="element-517"></div>
    {description && <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed" data-id="element-518">
        {description}
      </p>}
  </motion.div>;
/* ─── FOOD & DRINK ─── */
const portalBites = [{
  portal: 'Akali Phula Singh',
  bite: 'Warrior\u2019s Fuel \u2014 a bold, spiced amuse-bouche with smoked chili and cumin crunch',
  drink: 'Saffron lassi with black pepper'
}, {
  portal: 'Lehna Singh Majithia',
  bite: 'Builder\u2019s Precision \u2014 layered dal makhani tartlet with micro greens',
  drink: 'Cardamom cold brew'
}, {
  portal: 'Hari Singh Nalwa',
  bite: 'Frontier Strength \u2014 lamb seekh with pomegranate and mint',
  drink: 'Kashmiri kahwa'
}, {
  portal: 'Fakir Azizuddin',
  bite: 'Diplomatic Elegance \u2014 saffron paneer with rose petal and pistachio',
  drink: 'Rose sherbet with edible gold'
}, {
  portal: 'Maharani Jind Kaur',
  bite: 'Mother\u2019s Resilience \u2014 kheer with jaggery, topped with silver leaf',
  drink: 'Masala chai with honey'
}];
export function FoodDrinkSection() {
  const [expanded, setExpanded] = useState(false);
  return <section id="food-drink" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-519">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/10 to-transparent" data-id="element-520"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-521">
        {sectionHeader('Section 07', 'Food &', 'Drink', 'Every bite is synchronized with the story. Every drink is a chapter.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-522">
          <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-523">
            Service Philosophy
          </h3>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-524">
            The culinary program is not a separate dining experience — it is
            woven into the narrative. Each portal activation is accompanied by a
            historically researched bite and drink that connects guests to the
            character&#39;s world. Food arrives as the story demands, not on a
            fixed schedule.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-525">
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-526">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-527">
                Public Experience
              </p>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-528">
                5–7 courses synchronized with portal activations. Shared tables.
                Communal dining in the spirit of langar.
              </p>
            </div>
            <div className="border-l-2 border-[#d4a017]/20 pl-5" data-id="element-529">
              <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-2 font-medium" data-id="element-530">
                Premium / Private
              </p>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-531">
                Extended tasting menu with wine/cocktail pairings. Private
                hosted tables. Chef interaction and behind-the-scenes access.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Portal bites */}
        <div className="space-y-3" data-id="element-532">
          {portalBites.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.06
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6 grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr] gap-4 items-center" data-id="element-533">
              <p className="text-[#d4a017]/70 text-sm font-medium" data-id="element-534">
                {item.portal}
              </p>
              <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-535">
                {item.bite}
              </p>
              <p className="text-[#c2a878]/50 text-xs font-light italic" data-id="element-536">
                {item.drink}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
/* ─── MUSIC & SOUND ─── */
export function MusicSoundSection() {
  return <section id="music-sound" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-537">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-538">
        {sectionHeader('Section 08', 'Music &', 'Sound', 'A living soundtrack that breathes with the story — from whispered prayers to thundering cavalry.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-539">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-540">
            <MusicIcon className="w-8 h-8 text-[#d4a017]/40 mb-4" data-id="element-541" />
            <h3 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-542">
              Soundtrack Logic
            </h3>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-543">
              The soundtrack is not background music — it is a spatial audio
              experience that moves through the room. Each portal has its own
              musical identity, building from ambient textures to full
              orchestral moments. The sound design uses directional speakers to
              create intimate and expansive moments within the same space.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-544">
            <MusicIcon className="w-8 h-8 text-[#d4a017]/40 mb-4" data-id="element-545" />
            <h3 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-546">
              Character Themes
            </h3>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-547">
              Each of the five portal characters has a unique musical theme — a
              leitmotif that evolves throughout the evening. Akali Phula
              Singh&#39;s theme is percussive and fierce. Fakir Azizuddin&#39;s
              is melodic and refined. Maharani Jind Kaur&#39;s is tender and
              powerful. These themes weave together in the final reveal.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-548">
            <MusicIcon className="w-8 h-8 text-[#d4a017]/40 mb-4" data-id="element-549" />
            <h3 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-550">
              Portal Activation Sounds
            </h3>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-551">
              Each portal activation begins with a signature sound — a sonic
              trigger that tells guests something is about to happen. These
              range from a deep resonant gong to a whispered prayer, each
              calibrated to the character&#39;s emotional register.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8" data-id="element-552">
            <MusicIcon className="w-8 h-8 text-[#d4a017]/40 mb-4" data-id="element-553" />
            <h3 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-554">
              Final Reveal Score
            </h3>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-555">
              The final reveal is scored as a full orchestral crescendo — all
              five character themes converging into a single unified
              composition. The sound fills the entire room from every direction,
              creating a moment of collective awe.
            </p>
          </motion.div>
        </div>

        {/* Sound Design Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-556">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-557">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-558">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/6yMbfo4PUJo" title="Sound Design — Life in the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-559"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-560">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-561">
                Sound Design Explainer
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-562">
                How the spatial soundtrack, character themes, and portal
                activation sounds create an immersive audio world.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── AROMA / SCENT ─── */
export function AromaScentSection() {
  return <section id="aroma-scent" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-563">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a2e]/10 to-transparent" data-id="element-564"></div>
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-565">
        {sectionHeader('Section 09', 'Aroma /', 'Scent', 'The invisible layer of immersion — scent triggers memory, emotion, and presence.')}

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-8 md:p-10 mb-8" data-id="element-566">
          <h3 className="font-serif text-2xl text-[#f5f0e8] mb-4" data-id="element-567">
            Why Scent Matters
          </h3>
          <p className="text-[#f5f0e8]/50 font-light leading-relaxed" data-id="element-568">
            Scent is the most powerful memory trigger in the human brain. A
            single aroma can transport you to a specific place and time more
            effectively than any visual or sound. In Life in the Sikh Kingdoms,
            scent is not decoration — it is a narrative tool that deepens
            immersion and creates lasting emotional memories.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-569">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6" data-id="element-570">
            <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-3 font-medium" data-id="element-571">
              Diffuser Logic
            </p>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-572">
              Scent diffusers are positioned at each portal activation zone.
              They release precise, timed bursts synchronized with the visual
              and audio narrative. Scents dissipate within 30–45 seconds,
              ensuring each portal has its own distinct aromatic identity
              without blending.
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-6" data-id="element-573">
            <p className="text-[#d4a017] text-xs tracking-wider uppercase mb-3 font-medium" data-id="element-574">
              Safety &amp; Subtlety
            </p>
            <p className="text-[#f5f0e8]/40 text-sm font-light leading-relaxed" data-id="element-575">
              All scents are hypoallergenic, food-grade, and tested for
              sensitivity. The approach is subtle — guests should feel
              transported, not overwhelmed. Scent intensity is calibrated to be
              noticeable but never dominant, always serving the story rather
              than competing with it.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="space-y-3" data-id="element-576">
          <p className="text-[#d4a017]/50 text-[10px] tracking-[0.2em] uppercase mb-4" data-id="element-577">
            Portal-Specific Aromas
          </p>
          {[{
          portal: 'Akali Phula Singh',
          scent: 'Gunpowder, earth, and smoked spice — the battlefield'
        }, {
          portal: 'Lehna Singh Majithia',
          scent: 'Heated metal, sandalwood, and fresh-cut stone — the forge'
        }, {
          portal: 'Hari Singh Nalwa',
          scent: 'Pine, cold mountain air, and leather — the frontier'
        }, {
          portal: 'Fakir Azizuddin',
          scent: 'Rose, amber, and aged parchment — the diplomatic chamber'
        }, {
          portal: 'Maharani Jind Kaur',
          scent: "Jasmine, warm milk, and ink — the queen's chamber"
        }].map((item, i) => <div key={i} className="border border-[#d4a017]/10 bg-[#0f0f23]/30 p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-578">
              <p className="text-[#d4a017]/70 text-sm font-medium sm:w-48 flex-shrink-0" data-id="element-579">
                {item.portal}
              </p>
              <p className="text-[#f5f0e8]/40 text-sm font-light italic" data-id="element-580">
                {item.scent}
              </p>
            </div>)}
        </motion.div>

        {/* Scent & Aroma Video */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mt-12" data-id="element-581">
          <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-582">
            <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-583">
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/mHKhQCslj_U" title="Scent & Aroma — Life in the Sikh Kingdoms" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-584"></iframe>
            </div>
            <div className="p-6 border-t border-[#d4a017]/10" data-id="element-585">
              <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-586">
                Scent &amp; Aroma Explainer
              </h3>
              <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-587">
                How portal-specific aromas deepen immersion — the science, the
                design, and the emotional impact.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
/* ─── PRODUCTION & GANTT ─── */
const milestones = [{
  phase: 'Pre-Production',
  period: 'Jan – Mar 2026',
  items: ['Script & storyboard finalization', 'Venue technical assessment', 'Culinary R&D and menu development', 'Sound design composition']
}, {
  phase: 'Production',
  period: 'Mar – Apr 2026',
  items: ['360° animation rendering', 'Floor projection mapping', 'Scent system installation', 'Rehearsals and tech integration']
}, {
  phase: 'Soft Launch',
  period: 'May 2026',
  items: ['Prototype event for founders', 'Press preview night', 'Creator and influencer previews', 'Feedback integration']
}, {
  phase: 'Public Run',
  period: 'May – Dec 2026',
  items: ['Monthly public experiences', 'Private and corporate events', 'School and community nights', 'Ongoing optimization']
}];
export function ProductionSection() {
  return <section id="production" className="py-24 md:py-32 bg-[#0a0a0f] relative overflow-hidden" data-id="element-588">
      <div className="container mx-auto px-6 relative z-10 max-w-5xl" data-id="element-589">
        {sectionHeader('Section 10', 'Production &', 'Gantt', 'Workstreams, phases, milestones, and the critical path to opening night.')}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-590">
          {milestones.map((milestone, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: i * 0.1
        }} className="border border-[#d4a017]/15 bg-[#0f0f23]/40 p-7" data-id="element-591">
              <div className="flex items-center gap-3 mb-4" data-id="element-592">
                <span className="font-serif text-2xl text-[#d4a017]/30" data-id="element-593">
                  {i + 1}
                </span>
                <div data-id="element-594">
                  <h4 className="font-serif text-lg text-[#f5f0e8]" data-id="element-595">
                    {milestone.phase}
                  </h4>
                  <p className="text-[#d4a017]/50 text-xs tracking-wider uppercase font-light" data-id="element-596">
                    {milestone.period}
                  </p>
                </div>
              </div>
              <ul className="space-y-2" data-id="element-597">
                {milestone.items.map((item, j) => <li key={j} className="flex items-start gap-2 text-[#f5f0e8]/40 text-sm font-light" data-id="element-598">
                    <span className="w-1 h-1 rounded-full bg-[#d4a017]/40 mt-2 flex-shrink-0" data-id="element-599"></span>
                    {item}
                  </li>)}
              </ul>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="border border-[#d4a017]/20 bg-[#d4a017]/5 p-8 text-center" data-id="element-600">
          <CalendarIcon className="w-8 h-8 text-[#d4a017]/40 mx-auto mb-4" data-id="element-601" />
          <h4 className="font-serif text-xl text-[#f5f0e8] mb-2" data-id="element-602">
            Full Production Gantt
          </h4>
          <p className="text-[#f5f0e8]/40 text-sm font-light mb-6" data-id="element-603">
            Download the complete production schedule with all workstreams and
            dependencies.
          </p>
          <a href="#downloads" className="inline-flex items-center gap-2 border border-[#d4a017]/30 hover:border-[#d4a017] text-[#d4a017]/70 hover:text-[#d4a017] px-6 py-2.5 text-xs tracking-[0.15em] uppercase font-light transition-all duration-300" data-id="element-604">
            <DownloadIcon className="w-3.5 h-3.5" data-id="element-605" />
            View in Downloads
          </a>
        </motion.div>
      </div>
    </section>;
}